import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { teacherBalanceApi } from '@/api/teacherBalance'
import { getErrorMessage } from '@/utils/error'
import type { BalanceTopupRequest, TopupPaymentMethod } from '@/types/tts'

interface TopupRequestFormProps {
  onCreated: (request: BalanceTopupRequest) => void
}

export function TopupRequestForm({ onCreated }: TopupRequestFormProps) {
  const { t } = useTranslation()
  const [amount, setAmount] = useState('')
  const [paymentMethod, setPaymentMethod] = useState<TopupPaymentMethod>('bank_transfer')
  const [note, setNote] = useState('')
  const [proof, setProof] = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const request = await teacherBalanceApi.createTopupRequest({
        amount,
        payment_method: paymentMethod,
        note: note || undefined,
        proof,
      })
      setAmount('')
      setNote('')
      setProof(null)
      onCreated(request)
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={2}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <TextField
          label={t('balance.topup.amount', 'Сумма, ₽')}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          type="number"
          required
          inputProps={{ min: 1, max: 1000000, step: '0.01' }}
          size="small"
        />
        <TextField
          select
          label={t('balance.topup.paymentMethod', 'Способ оплаты')}
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value as TopupPaymentMethod)}
          size="small"
        >
          <MenuItem value="bank_transfer">
            {t('balance.topup.methods.bank_transfer', 'Банковский перевод')}
          </MenuItem>
          <MenuItem value="manual">
            {t('balance.topup.methods.manual', 'Другое (вручную)')}
          </MenuItem>
        </TextField>
        <TextField
          label={t('balance.topup.note', 'Комментарий')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          multiline
          minRows={2}
          size="small"
          inputProps={{ maxLength: 1000 }}
        />
        <Button variant="outlined" component="label" size="small">
          {proof
            ? proof.name
            : t('balance.topup.proof', 'Прикрепить чек (jpg, png, pdf)')}
          <input
            hidden
            type="file"
            accept=".jpg,.jpeg,.png,.pdf"
            onChange={(e) => setProof(e.target.files?.[0] ?? null)}
          />
        </Button>
        <Button type="submit" variant="contained" disabled={submitting || !amount}>
          {t('balance.topup.submit', 'Отправить заявку')}
        </Button>
      </Stack>
    </Box>
  )
}
